import { useCallback, useMemo, useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Loading from 'components/Loading';
import SearchableFilter from 'components/SearchableFilter';
import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import useSort from 'utils/useSort';
import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import * as ManagementLayout from 'components/ManagementLayout';

import { NoWrapHeaderTable } from '../components/styles';
import { useManagedPlatformAccountsQuery } from './hooks/useManagedPlatformAccountsQuery';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from '../Teams/hooks/useTeamsFilters';

import useAccountsFilters from './hooks/useAccountsFilters';
import { generateColumns } from './columns';

export type ManagedFleet = ExtractFromArray<
  ManagedPlatformAccountsQuery['managedPlatformAccounts']['collection']
>;

const Fleets = () => {
  const { isSuperUser } = useCurrentUserContext();

  const { pageParams, setPageParams } = useManagementPageContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedPlatformAccountsQuery({
    variables: {
      sort,
      page,
      ids: pageParams.accountIds,
      sentinelTeamsIds: pageParams.teamIds,
      oemSegmentsIds: pageParams.oemSegmentIds,
    },
  });

  const handleOnSubmitOemSegments = useCallback(
    ({ value }) =>
      setPageParams((oldState) => ({
        ...oldState,
        oemSegmentIds: value as string[],
      })),
    [setPageParams],
  );

  const handleOnSubmitTeams = useCallback(
    ({ value }) =>
      setPageParams((oldState) => ({
        ...oldState,
        teamIds: value as string[],
      })),
    [setPageParams],
  );

  const handleOnSubmitAccounts = useCallback(
    ({ value }) =>
      setPageParams((oldState) => ({
        ...oldState,
        accountIds: value as string[],
      })),
    [setPageParams],
  );

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleOnSubmitOemSegments,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    onSubmit: handleOnSubmitTeams,
  });

  const accountsFilters = useAccountsFilters({
    value: pageParams.accountIds,
    onSubmit: handleOnSubmitAccounts,
  });

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const tableData = data?.managedPlatformAccounts.collection ?? [];
  const pagination = data?.managedPlatformAccounts.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          {isSuperUser && (
            <Button text={t`Create Account`} icon={Plus} size="small" />
          )}
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <P size="small" marginRight={1}>
            <Trans>Search Fleets by Name:</Trans>
          </P>
          <SearchInput value="" hideLabel name="searchByFleet" size="small" />
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          <SearchableFilter
            popoverLabel={t`OEM Segments`}
            placement="bottom-start"
            {...oemSegmentsFilterProps}
          />
          <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
          <SearchableFilter popoverLabel={t`Accounts`} {...accountsFilters} />
        </ManagementLayout.TabBody.Content.Filters>
        {loading && <Loading />}
        {!loading && (
          <NoWrapHeaderTable
            height="100%"
            scroll={['x', 'y']}
            data={tableData}
            columns={columns}
            onSort={onSort}
            defaultSort={defaultSort}
            footer={() => (
              <PaginationFooter
                // @ts-expect-error - JS file
                title={t`Accounts`}
                setCurrentPage={setPage}
                totalOnPage={tableData.length}
                page={pagination?.page}
                perPage={pagination?.perPage}
                totalEntries={pagination?.totalEntries}
                totalPages={pagination?.totalPages}
              />
            )}
          />
        )}
      </ManagementLayout.TabBody.Content.Container>
    </ManagementLayout.TabBody.Container>
  );
};

export default Fleets;
